import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, CheckCircle2, Circle, XCircle } from "lucide-react";

/**
 * Peppol Turn 2ter — Page Company admin (simplifiée).
 *
 * Les inputs d'identité (VAT, adresse structurée) ont été déplacés vers
 * `Paramètres → Entreprise` (source unique). Cette page conserve :
 *  - État Peppol (badge onboarding, supplier_id, reject_reason)
 *  - Checklist ✓/✗ des champs requis pour l'onboarding, avec lien vers Entreprise
 */
const STATUS_BADGES = {
  null: { label: "Non configuré", color: "bg-slate-700 text-slate-200", icon: Circle },
  CREATED: { label: "Créé", color: "bg-blue-900/40 text-blue-200", icon: Circle },
  APPROVED: { label: "En cours", color: "bg-amber-900/40 text-amber-200", icon: Circle },
  ONBOARDED: { label: "Onboardé ✓", color: "bg-green-900/40 text-green-200", icon: CheckCircle2 },
  REJECTED: { label: "Rejeté ⚠", color: "bg-red-900/40 text-red-200", icon: AlertTriangle },
  OFFBOARDED: { label: "Désactivé", color: "bg-slate-700 text-slate-200", icon: Circle },
};

const CHECKLIST_FIELDS = [
  { key: "name", label: "Nom d'entreprise" },
  { key: "vat_number", label: "Numéro TVA" },
  { key: "iban", label: "IBAN" },
  { key: "street", label: "Rue" },
  { key: "street_number", label: "Numéro" },
  { key: "zip_code", label: "Code postal" },
  { key: "city", label: "Ville" },
  { key: "contact_email", label: "Email de contact" },
  { key: "phone", label: "Téléphone" },
];

export default function PeppolSettingsPage() {
  const { state } = useAuth();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/company/settings")
      .then(({ data }) => setSettings(data))
      .catch((e) => toast.error(formatApiError(e)));
    // Bug #2 fix (14/09/2026) — refetch quand company change dans le même onglet.
  }, [state?.company?.id]);

  if (!settings) return <div className="text-slate-400" data-testid="peppol-loading">Chargement…</div>;

  const status = settings.peppol_wk_onboarding_status;
  const badgeCfg = STATUS_BADGES[status] || STATUS_BADGES["null"];
  const IconEl = badgeCfg.icon;

  const missing = CHECKLIST_FIELDS.filter((f) => !(settings[f.key] || "").toString().trim());
  const structuredAddress = [settings.street, settings.street_number, settings.zip_code, settings.city, settings.country]
    .filter(Boolean).join(", ");

  return (
    <div className="space-y-6" data-testid="peppol-settings-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-100">
          Facturation électronique (Peppol)
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          Envoi et réception de factures via le réseau Peppol européen. L&apos;onboarding chez
          Wolters Kluwer Flowin est déclenché par le support Wérko.
        </p>
      </div>

      {/* État Peppol */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm text-slate-300">État Peppol</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3" data-testid="peppol-status-badge">
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${badgeCfg.color}`}
            >
              <IconEl className="h-3 w-3" /> {badgeCfg.label}
            </span>
            {settings.peppol_wk_supplier_id && (
              <span className="font-mono text-xs text-slate-500">
                supplier: {settings.peppol_wk_supplier_id.slice(0, 8)}…
              </span>
            )}
          </div>
          {status === "REJECTED" && (
            <div
              className="rounded-md border border-red-800/50 bg-red-950/40 p-3 text-sm text-red-200"
              data-testid="peppol-reject-banner"
            >
              <b>Onboarding rejeté</b> — Raison : {settings.peppol_wk_reject_reason || "non précisée"}.
              Corrigez vos informations dans <Link to="/settings/company" className="underline">
              Paramètres → Entreprise</Link> puis contactez le support Wérko pour une nouvelle
              tentative.
            </div>
          )}
          {!status && (
            <p className="text-sm text-slate-400">
              L&apos;onboarding Peppol n&apos;a pas encore été déclenché pour votre entreprise.
              Complétez d&apos;abord la checklist ci-dessous — le support Wérko déclenchera
              ensuite l&apos;onboarding chez Wolters Kluwer Flowin.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Note redirection Entreprise */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm text-slate-300">Informations d&apos;entreprise</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-400" data-testid="peppol-info-redirect">
            Les données requises pour l&apos;onboarding Peppol (numéro TVA, adresse structurée)
            se configurent dans{" "}
            <Link to="/settings/company" className="text-[#C4553B] underline hover:text-[#A8402C]">
              Paramètres → Entreprise
            </Link>
            {" "}(bloc « Identité légale »).
          </p>

          <div className="rounded-md border border-slate-800 bg-slate-950/40 p-4">
            <div className="mb-3 text-xs font-medium text-slate-400">État actuel de vos infos</div>
            <ul className="space-y-2 text-sm" data-testid="peppol-info-summary">
              <li className="flex items-center gap-2">
                {settings.vat_number ? (
                  <><CheckCircle2 className="h-4 w-4 text-green-400" />
                  <span className="text-slate-300">Numéro TVA :</span>
                  <span className="font-mono text-slate-200">{settings.vat_number}</span></>
                ) : (
                  <><XCircle className="h-4 w-4 text-red-400" />
                  <span className="text-red-300">Numéro TVA : non renseigné</span></>
                )}
              </li>
              <li className="flex items-center gap-2">
                {structuredAddress && settings.street ? (
                  <><CheckCircle2 className="h-4 w-4 text-green-400" />
                  <span className="text-slate-300">Adresse :</span>
                  <span className="text-slate-200">{structuredAddress}</span></>
                ) : (
                  <><XCircle className="h-4 w-4 text-red-400" />
                  <span className="text-red-300">Adresse structurée : non renseignée</span></>
                )}
              </li>
              <li className="flex items-center gap-2">
                {settings.iban ? (
                  <><CheckCircle2 className="h-4 w-4 text-green-400" />
                  <span className="text-slate-300">IBAN :</span>
                  <span className="font-mono text-slate-200">{settings.iban}</span></>
                ) : (
                  <><XCircle className="h-4 w-4 text-red-400" />
                  <span className="text-red-300">IBAN : non renseigné</span></>
                )}
              </li>
            </ul>
          </div>

          <div className="rounded-md border border-slate-800 bg-slate-950/40 p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium text-slate-400">
                Checklist prêt à onboarder
              </span>
              <span className={`text-xs font-semibold ${missing.length === 0 ? "text-green-400" : "text-amber-400"}`}
                data-testid="peppol-checklist-status">
                {missing.length === 0 ? "✓ Complète" : `${missing.length} manquant(s)`}
              </span>
            </div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {CHECKLIST_FIELDS.map((f) => {
                const ok = !!(settings[f.key] || "").toString().trim();
                return (
                  <li key={f.key} className="flex items-center gap-2" data-testid={`peppol-checklist-${f.key}`}>
                    {ok
                      ? <CheckCircle2 className="h-3 w-3 text-green-400" />
                      : <XCircle className="h-3 w-3 text-red-400" />}
                    <span className={ok ? "text-slate-300" : "text-red-300"}>{f.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
